import { useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import type { RolePermissions } from '@/contexts/AuthContext';
import { DEFAULT_PERMISSIONS } from '@/contexts/AuthContext';

const ALL_PERMISSIONS_ZERO: RolePermissions = {
  dashboard: false,
  reception: { shifts: false, checkin: false, checkout: false, billing: false, cash: false, logs: false, reports: false },
  bookings: { rooms: false, new: false, manage: false, guests: false, billing: false, reports: false },
  inventory: { stock: false, purchases: false, vendors: false, reports: false },
  canteen: { menu: false, billing: false, history: false, reports: false },
  manageUsers: false,
};

interface PermissionGroup {
  label: string;
  icon: string;
  items: { key: string; label: string; parent?: string }[];
}

const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    label: 'Dashboard',
    icon: 'ri-dashboard-line',
    items: [{ key: 'dashboard', label: 'Dashboard Access' }],
  },
  {
    label: 'Reception',
    icon: 'ri-customer-service-2-line',
    items: [
      { key: 'shifts', label: 'Shifts', parent: 'reception' },
      { key: 'checkin', label: 'Check-in', parent: 'reception' },
      { key: 'checkout', label: 'Check-out', parent: 'reception' },
      { key: 'billing', label: 'Billing & Receipts', parent: 'reception' },
      { key: 'cash', label: 'Cash Tracking', parent: 'reception' },
      { key: 'logs', label: 'Activity Logs', parent: 'reception' },
      { key: 'reports', label: 'Reports', parent: 'reception' },
    ],
  },
  {
    label: 'Bookings',
    icon: 'ri-calendar-check-line',
    items: [
      { key: 'rooms', label: 'Room Status Grid', parent: 'bookings' },
      { key: 'new', label: 'New Booking', parent: 'bookings' },
      { key: 'manage', label: 'Manage Bookings', parent: 'bookings' },
      { key: 'guests', label: 'Guest Directory', parent: 'bookings' },
      { key: 'billing', label: 'GST Billing', parent: 'bookings' },
      { key: 'reports', label: 'Reports', parent: 'bookings' },
    ],
  },
  {
    label: 'Inventory',
    icon: 'ri-box-3-line',
    items: [
      { key: 'stock', label: 'Stock Levels', parent: 'inventory' },
      { key: 'purchases', label: 'Purchases', parent: 'inventory' },
      { key: 'vendors', label: 'Vendors', parent: 'inventory' },
      { key: 'reports', label: 'Reports', parent: 'inventory' },
    ],
  },
  {
    label: 'Canteen',
    icon: 'ri-restaurant-line',
    items: [
      { key: 'menu', label: 'Menu Management', parent: 'canteen' },
      { key: 'billing', label: 'Billing & KOT', parent: 'canteen' },
      { key: 'history', label: 'Bill History', parent: 'canteen' },
      { key: 'reports', label: 'Reports', parent: 'canteen' },
    ],
  },
  {
    label: 'Admin',
    icon: 'ri-shield-user-line',
    items: [{ key: 'manageUsers', label: 'Manage Users (Add/Edit/Delete)' }],
  },
];

function getPermissionValue(perms: RolePermissions, item: PermissionGroup['items'][0]): boolean {
  if (!item.parent) {
    return (perms as Record<string, boolean>)[item.key] ?? false;
  }
  return (perms[item.parent as keyof RolePermissions] as Record<string, boolean>)?.[item.key] ?? false;
}

function setPermissionValue(
  perms: RolePermissions,
  item: PermissionGroup['items'][0],
  value: boolean
): RolePermissions {
  const result = { ...perms };
  if (!item.parent) {
    (result as Record<string, boolean>)[item.key] = value;
  } else {
    const parent = item.parent as keyof RolePermissions;
    result[parent] = { ...(result[parent] as Record<string, boolean>), [item.key]: value } as RolePermissions[keyof RolePermissions];
  }
  return result;
}

const ROLE_BADGE_COLORS: Record<string, string> = {
  admin: 'bg-red-100 text-red-700 border-red-200',
  manager: 'bg-amber-100 text-amber-700 border-amber-200',
  receptionist: 'bg-sky-100 text-sky-700 border-sky-200',
  housekeeping: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  accountant: 'bg-violet-100 text-violet-700 border-violet-200',
};

function getRoleBadgeColor(role: string): string {
  return ROLE_BADGE_COLORS[role] || 'bg-teal-100 text-teal-700 border-teal-200';
}

export default function PermissionsPage() {
  const {
    permissions,
    permissionMap,
    allRoles,
    updateRolePermissions,
    resetAllPermissions,
    getRoleLabel,
    addCustomRole,
    removeCustomRole,
    isBuiltInRole,
  } = useAuth();
  const { addToast } = useApp();
  const [selectedRole, setSelectedRole] = useState<string>(allRoles[0] || 'manager');

  // Add role modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRoleId, setNewRoleId] = useState('');
  const [newRoleLabel, setNewRoleLabel] = useState('');

  // Delete confirm
  const [deleteConfirmRole, setDeleteConfirmRole] = useState<string | null>(null);

  const canManage = permissions?.manageUsers ?? false;

  const currentPerms = permissionMap[selectedRole] || ALL_PERMISSIONS_ZERO;
  const defaultPerms = DEFAULT_PERMISSIONS[selectedRole];

  const isModified = useMemo(() => {
    if (!defaultPerms) return true; // Custom role — always "modified" from default
    return JSON.stringify(currentPerms) !== JSON.stringify(defaultPerms);
  }, [currentPerms, defaultPerms]);

  const handleToggle = (item: PermissionGroup['items'][0]) => {
    const currentVal = getPermissionValue(currentPerms, item);
    const newPerms = setPermissionValue(currentPerms, item, !currentVal);
    updateRolePermissions(selectedRole, newPerms);
    addToast('success', `${item.label}: ${!currentVal ? 'Granted' : 'Revoked'} for ${getRoleLabel(selectedRole)}`);
  };

  const handleReset = () => {
    if (isBuiltInRole(selectedRole) && defaultPerms) {
      updateRolePermissions(selectedRole, defaultPerms);
      addToast('info', `Permissions reset to default for ${getRoleLabel(selectedRole)}`);
    } else {
      // For custom roles, reset to all false except dashboard
      const emptyPerms: RolePermissions = {
        dashboard: true,
        reception: { shifts: false, checkin: false, checkout: false, billing: false, cash: false, logs: false, reports: false },
        bookings: { rooms: false, new: false, manage: false, guests: false, billing: false, reports: false },
        inventory: { stock: false, purchases: false, vendors: false, reports: false },
        canteen: { menu: false, billing: false, history: false, reports: false },
        manageUsers: false,
      };
      updateRolePermissions(selectedRole, emptyPerms);
      addToast('info', `Permissions cleared for ${getRoleLabel(selectedRole)}`);
    }
  };

  const handleResetAll = () => {
    resetAllPermissions();
    addToast('info', 'All roles reset to default permissions');
    setSelectedRole('manager');
  };

  const handleAddRole = () => {
    const cleanId = newRoleId.toLowerCase().replace(/[^a-z0-9-]/g, '-');
    if (cleanId.length < 3) {
      addToast('error', 'Role ID must be at least 3 characters');
      return;
    }
    if (!newRoleLabel.trim()) {
      addToast('error', 'Please enter a display name for the role');
      return;
    }
    const success = addCustomRole(cleanId, newRoleLabel.trim());
    if (success) {
      addToast('success', `Role "${newRoleLabel.trim()}" created! Now configure its permissions.`);
      setShowAddModal(false);
      setNewRoleId('');
      setNewRoleLabel('');
      setSelectedRole(cleanId);
    } else {
      addToast('error', 'Role ID already exists or is invalid. Try a different name.');
    }
  };

  const handleDeleteRole = () => {
    if (!deleteConfirmRole) return;
    const label = getRoleLabel(deleteConfirmRole);
    const success = removeCustomRole(deleteConfirmRole);
    if (success) {
      addToast('success', `Role "${label}" deleted`);
      if (selectedRole === deleteConfirmRole) {
        setSelectedRole('manager');
      }
      setDeleteConfirmRole(null);
    } else {
      addToast('error', `Cannot delete "${label}" — users are still assigned to this role`);
      setDeleteConfirmRole(null);
    }
  };

  // Refresh selected role if it got deleted
  if (!allRoles.includes(selectedRole) && allRoles.length > 0) {
    setSelectedRole(allRoles[0]);
  }

  if (!canManage) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-shield-user-line text-red-500 text-3xl" />
          </div>
          <h2 className="text-xl font-semibold text-navy-800 mb-2">Access Denied</h2>
          <p className="text-navy-500 text-sm">Only administrators can manage permissions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-50/30">
      {/* Page Header */}
      <div className="bg-white border-b border-navy-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-4 md:py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-navy-800">Permission Management</h1>
              <p className="text-xs text-navy-500 mt-0.5">Click checkboxes to grant or revoke access. Add custom roles with one click.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setNewRoleId('');
                  setNewRoleLabel('');
                  setShowAddModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line" />
                Add New Role
              </button>
              <button
                onClick={handleResetAll}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-restart-line" />
                Reset All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Role Tabs */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-4 md:py-6">
        <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-navy-100 px-4 py-3 flex items-center gap-1.5 overflow-x-auto">
            {allRoles.map((role) => {
              const isModifiedRole = isBuiltInRole(role)
                ? JSON.stringify(permissionMap[role] || DEFAULT_PERMISSIONS[role]) !== JSON.stringify(DEFAULT_PERMISSIONS[role])
                : true;
              const builtIn = isBuiltInRole(role);
              return (
                <div key={role} className="relative group flex-shrink-0">
                  <button
                    onClick={() => setSelectedRole(role)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      selectedRole === role
                        ? 'bg-navy-700 text-white'
                        : 'text-navy-600 hover:bg-navy-50'
                    }`}
                  >
                    <span>{getRoleLabel(role)}</span>
                    {!builtIn && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${getRoleBadgeColor(role)}`}>
                        Custom
                      </span>
                    )}
                    {isModifiedRole && (
                      <span className={`w-1.5 h-1.5 rounded-full ${selectedRole === role ? 'bg-yellow-400' : 'bg-yellow-500'}`} />
                    )}
                  </button>
                  {/* Delete button for custom roles */}
                  {!builtIn && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmRole(role);
                      }}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      title={`Delete ${getRoleLabel(role)}`}
                    >
                      <i className="ri-close-line text-[8px]" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Permission Grid */}
          <div className="p-4 md:p-6 space-y-6">
            {PERMISSION_GROUPS.map((group) => (
              <div key={group.label}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 flex items-center justify-center text-navy-400">
                    <i className={group.icon} />
                  </span>
                  <h3 className="text-sm font-semibold text-navy-700 uppercase tracking-wide">
                    {group.label}
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {group.items.map((item) => {
                    const isChecked = getPermissionValue(currentPerms, item);
                    return (
                      <label
                        key={item.key}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                          isChecked
                            ? 'border-emerald-200 bg-emerald-50/50'
                            : 'border-navy-100 hover:border-navy-200 bg-white'
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isChecked
                              ? 'bg-emerald-500 border-emerald-500'
                              : 'border-navy-300'
                          }`}
                        >
                          {isChecked && (
                            <i className="ri-check-line text-white text-xs font-bold" />
                          )}
                        </div>
                        <span
                          className={`text-sm ${
                            isChecked ? 'text-navy-800 font-medium' : 'text-navy-500'
                          }`}
                        >
                          {item.label}
                        </span>
                        {/* Hidden checkbox for accessibility */}
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggle(item)}
                          className="sr-only"
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Footer Actions */}
          <div className="border-t border-navy-100 px-4 md:px-6 py-4 flex items-center justify-between bg-navy-50/30">
            <div className="text-xs text-navy-400">
              {isModified ? (
                <span className="text-amber-600 font-medium">
                  <i className="ri-error-warning-line mr-1" />
                  {isBuiltInRole(selectedRole) ? 'Modified from default' : 'Custom role'}
                </span>
              ) : (
                <span className="text-emerald-600">
                  <i className="ri-check-line mr-1" />
                  Default permissions applied
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isBuiltInRole(selectedRole) && (
                <button
                  onClick={() => setDeleteConfirmRole(selectedRole)}
                  className="flex items-center gap-2 px-4 py-2 border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line" />
                  Delete Role
                </button>
              )}
              <button
                onClick={handleReset}
                disabled={!isModified}
                className="flex items-center gap-2 px-4 py-2 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <i className="ri-arrow-go-back-line" />
                {isBuiltInRole(selectedRole) ? 'Reset to Default' : 'Clear All Permissions'}
              </button>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-lightbulb-line text-amber-600" />
            </span>
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="space-y-1 text-amber-700">
                <li>&bull; <strong>Add New Role</strong> — Click the green button, enter a name like "Supervisor", and start configuring</li>
                <li>&bull; <strong>Tick a checkbox</strong> = That role can see and use that page</li>
                <li>&bull; <strong>Untick</strong> = That page disappears from their sidebar</li>
                <li>&bull; Changes save automatically &mdash; no extra button needed</li>
                <li>&bull; Yellow dot on role tab means permissions have been customized</li>
                <li>&bull; Hover over custom roles to see the delete button</li>
                <li>&bull; Built-in roles (Admin, Manager, etc.) cannot be deleted</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Add New Role Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-navy-800">Create New Role</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="w-8 h-8 rounded-lg hover:bg-navy-50 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-navy-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">
                  Role ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newRoleId}
                  onChange={(e) => setNewRoleId(e.target.value)}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g. supervisor, captain, chef"
                />
                <p className="text-xs text-navy-400 mt-1">Lowercase, no spaces. Used internally (3-20 chars).</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newRoleLabel}
                  onChange={(e) => setNewRoleLabel(e.target.value)}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g. Floor Supervisor, Head Chef"
                />
                <p className="text-xs text-navy-400 mt-1">This name will appear in the UI.</p>
              </div>
              <div className="p-3 bg-navy-50 rounded-lg">
                <p className="text-xs text-navy-500">
                  <i className="ri-information-line mr-1" />
                  After creating, you can configure its permissions using the checkboxes below. Only <strong>Dashboard</strong> is enabled by default.
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddRole}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer"
                >
                  Create Role
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmRole && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-xl">
            <div className="px-6 py-4 border-b border-navy-100">
              <h3 className="text-lg font-semibold text-navy-800">Delete Role</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-navy-600 mb-1">
                Are you sure you want to delete the role
              </p>
              <p className="text-sm font-semibold text-navy-800 mb-4">
                "{getRoleLabel(deleteConfirmRole)}"?
              </p>
              <p className="text-xs text-navy-500 mb-5">
                You can only delete roles that have <strong>no users</strong> assigned to them. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirmRole(null)}
                  className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteRole}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}